























import Component, { mixins } from "vue-class-component";
import { Location } from "vue-router";
import WithAuthUser from "@/mixins/WithAuthUser";
import providers from "@/lib/api";
import { Throttle } from "@/lib/decorators";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { getShrinkedNumber } from "@/lib/moneyHelpers";
import { getProfileUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { HideMenuHandlerData } from "@/types";
import { EVENTS } from "@/const";
import BaseAvatar from "@/components/BaseAvatar";
import BaseIcon from "@/components/BaseIcon";

interface MenuItem {
  label: string;
  name: string;
  to: Location;
  countNew?: number;
  exact?: boolean;
}

@Component({ name: "MainNavigationBar", components: { BaseIcon, BaseAvatar } })
export default class MainNavigationBar extends mixins(WithAuthUser) {
  public unreadMessageThreadsCount = 0;

  public get menuList(): MenuItem[] {
    if (!this.authUser) return [];
    return [
      {
        label: "sci-home",
        name: $t("Home"),
        to: {
          name: config.soloCreatorId !== null ? "profileBySoloCreatorId" : "home",
        },
        exact: true,
      },
      {
        label: "sci-account-circle",
        name: $t("Profile"),
        to: this.profileTo,
        exact: true,
      },
      {
        label: "sci-chat",
        name: $t("Messages"),
        to: {
          name: "messageThreads",
        },
        countNew: this.shrinkedUnreadCount,
      },
      ...(this.authUser.role === "member" && config.soloCreatorId === null
        ? [
            {
              label: "sci-search",
              name: $t("Search page"),
              to: {
                name: "index",
              },
              exact: true,
            },
          ]
        : [
            {
              label: "sci-bell",
              name: $t("Notifications"),
              to: {
                name: "notifications",
              },
              countNew: 0,
            },
          ]),
      {
        label: "sci-menu",
        name: $t("Settings"),
        to: {
          name: "more",
        },
      },
    ];
  }

  public get profileTo() {
    return getProfileUrl(this.authUserStrict);
  }

  public get avatarUrl(): string {
    if (!this.authUserStrict.avatar) return "";
    return MediaFilesController.getImageThumbnail(this.authUserStrict.avatar.urlPart, 76);
  }

  public get shrinkedUnreadCount() {
    return Number(getShrinkedNumber(this.unreadMessageThreadsCount)) ?? 0;
  }

  public async getUnreadCount() {
    this.unreadMessageThreadsCount = await providers.MessageThreadsProvider.getUnreadCount();
  }

  @Throttle(3000, { trailing: false })
  public receivedMessage() {
    this.getUnreadCount();
  }

  public hideMenuHandler(hideMenuHandlerData: HideMenuHandlerData) {
    const root = document.documentElement;
    if (hideMenuHandlerData.hide === undefined && hideMenuHandlerData.fullHide === undefined) {
      return;
    }
    if (hideMenuHandlerData?.hide || hideMenuHandlerData?.fullHide) {
      root.style.setProperty("--safe-bottom-position", "0px");
    } else {
      root.style.setProperty("--safe-bottom-position", "calc(56px + env(safe-area-inset-bottom))");
    }
  }

  public created() {
    this.hideMenuHandler({ hide: false });
    this.getUnreadCount();
    emitter.on<any>(EVENTS.MESSAGES_NEW_MESSAGE, this.receivedMessage);
    emitter.on<any>(EVENTS.MESSAGES_MARKED_READ, this.getUnreadCount);
    emitter.on<any>(EVENTS.MAIN_MENU_HIDE, this.hideMenuHandler);
  }

  public beforeDestroy() {
    this.hideMenuHandler({ hide: true });
    emitter.off<any>(EVENTS.MESSAGES_NEW_MESSAGE, this.receivedMessage);
    emitter.off<any>(EVENTS.MESSAGES_MARKED_READ, this.getUnreadCount);
    emitter.off<any>(EVENTS.MAIN_MENU_HIDE, this.hideMenuHandler);
  }
}
